import type { OmniHumanProviderConfigView } from "@actale/contracts";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "./api.js";
import { formatUpdatedAt } from "./story-utils.js";

function message(caught: unknown, fallback: string): string {
  return caught instanceof ApiError ? caught.message : fallback;
}

export function OmniHumanProviderConfigPanel({ refreshToken = 0 }: { refreshToken?: number }): React.ReactNode {
  const [config, setConfig] = useState<OmniHumanProviderConfigView | null>(null);
  const [accessKeyId, setAccessKeyId] = useState("");
  const [secretAccessKey, setSecretAccessKey] = useState("");
  const [showSecrets, setShowSecrets] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setConfig(await api.omniHumanProviderConfig());
      setAccessKeyId("");
      setSecretAccessKey("");
      setShowSecrets(false);
      setDeleteConfirm(false);
    } catch (caught) {
      setError(message(caught, "无法读取 OmniHuman 配置"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load, refreshToken]);

  async function save(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!config || !accessKeyId.trim() || !secretAccessKey.trim()) {
      setError("Access Key ID 和 Secret Access Key 必须同时填写");
      return;
    }
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const next = await api.updateOmniHumanProviderConfig({
        accessKeyId: accessKeyId.trim(),
        secretAccessKey: secretAccessKey.trim(),
        expectedUpdatedAt: config.updatedAt,
      });
      setConfig(next);
      setAccessKeyId("");
      setSecretAccessKey("");
      setShowSecrets(false);
      setDeleteConfirm(false);
      setNotice("OmniHuman AK/SK 已加密保存；本次没有调用模型、上传素材或产生费用。");
    } catch (caught) {
      setError(message(caught, "OmniHuman AK/SK 保存失败"));
    } finally {
      setSaving(false);
    }
  }

  async function remove(): Promise<void> {
    if (!config?.updatedAt || !deleteConfirm) return;
    setDeleting(true);
    setError("");
    setNotice("");
    try {
      await api.deleteOmniHumanProviderConfig(config.updatedAt);
      await load();
      setNotice("OmniHuman AK/SK 密文已删除；已有图片、语音和视频不会被删除。");
    } catch (caught) {
      setError(message(caught, "删除 OmniHuman 配置失败"));
    } finally {
      setDeleting(false);
    }
  }

  if (loading && !config) {
    return <div className="director-loading"><span /><strong>正在读取 OmniHuman 配置…</strong></div>;
  }
  if (!config) {
    return <div className="state-card"><div className="error-notice" role="alert"><span>!</span>{error || "OmniHuman 配置不可用"}</div><button className="secondary-button" onClick={() => void load()}>重新加载</button></div>;
  }

  const busy = loading || saving || deleting;
  const credentialsReady = Boolean(accessKeyId.trim() && secretAccessKey.trim());

  return (
    <form className="provider-config-form provider-omnihuman-card" onSubmit={(event) => void save(event)}>
      <header>
        <div>
          <span className={`provider-state-dot ${config.configured ? "ready" : "empty"}`} />
          <div><strong>OmniHuman 1.5 · 音频驱动人物视频</strong><small>{config.provider} · {config.region} · {config.service}</small></div>
        </div>
        <span className={`provider-config-status ${config.configured ? "ready" : "empty"}`}>{config.configured ? "已配置" : "未配置"}</span>
      </header>

      {error ? <div className="error-notice provider-inline-notice" role="alert"><span>!</span>{error}</div> : null}
      {notice ? <div className="director-notice provider-inline-notice" role="status">✓ {notice}</div> : null}
      {!config.encryptionReady ? (
        <div className="provider-security-alert provider-inline-notice" role="alert">
          <strong>服务器加密主密钥尚未就绪</strong>
          <span>在服务器配置主密钥并重启 API 后，才能保存火山视觉服务 AK/SK。</span>
        </div>
      ) : null}

      <div className="provider-form-grid">
        <label>
          <span>固定 Region / Service</span>
          <input value={`${config.region} / ${config.service}`} disabled readOnly />
        </label>
        <label>
          <span>固定 API 版本</span>
          <input value={config.apiVersion} disabled readOnly />
        </label>
        <label className="provider-wide-field">
          <span>固定接口</span>
          <input type="url" value={config.apiBaseUrl} disabled readOnly />
          <small>系统固定为火山视觉服务地址，后台不能改成其他地址。</small>
        </label>
        <label className="provider-wide-field">
          <span>固定提交动作</span>
          <input value={config.submitAction} disabled readOnly />
          <small>请求键：{config.requestKey}。仅供查看，不会提交生成任务。</small>
        </label>
        <label>
          <span>{config.configured ? "Access Key ID（填写后与 SK 一起更换）" : "Access Key ID"}</span>
          <input type={showSecrets ? "text" : "password"} value={accessKeyId} onChange={(event) => setAccessKeyId(event.target.value)} autoComplete="new-password" minLength={8} maxLength={256} placeholder={config.configured ? `已保存 ${config.accessKeyIdHint ?? "加密 AK"}` : "输入火山账号 Access Key ID"} required />
        </label>
        <label>
          <span>{config.configured ? "Secret Access Key（填写后与 AK 一起更换）" : "Secret Access Key"}</span>
          <div className="provider-secret-input">
            <input type={showSecrets ? "text" : "password"} value={secretAccessKey} onChange={(event) => setSecretAccessKey(event.target.value)} autoComplete="new-password" minLength={8} maxLength={8192} placeholder={config.configured ? `已保存 ${config.secretAccessKeyHint ?? "加密 SK"}` : "输入火山账号 Secret Access Key"} required />
            <button type="button" className="text-button" onClick={() => setShowSecrets(!showSecrets)}>{showSecrets ? "隐藏" : "显示"}</button>
          </div>
        </label>
      </div>

      <div className="provider-save-warning">
        <span aria-hidden="true">!</span>
        <p><strong>这里只保存配置，不会调用模型或产生费用</strong><small>保存配置不会上传图片、语音或视频素材。</small></p>
      </div>

      <footer>
        <span>{config.updatedAt ? `最近更新：${formatUpdatedAt(config.updatedAt)} · ${config.updatedBy?.displayName ?? "系统"}` : "尚无已保存配置"}</span>
        <button className="primary-button compact" disabled={busy || !config.encryptionReady || !credentialsReady}>{saving ? "正在加密保存…" : config.configured ? "安全更换 AK/SK" : "安全保存 AK/SK"}</button>
      </footer>

      {config.configured ? (
        <div className="provider-delete-zone">
          <strong>删除配置</strong>
          <small>只删除数据库中的 AK/SK 密文，不会删除任何人物图片、语音或视频。</small>
          {!deleteConfirm ? <button type="button" className="danger-text-button" onClick={() => setDeleteConfirm(true)}>删除 OmniHuman 配置</button> : (
            <div><button type="button" className="danger-button" disabled={busy} onClick={() => void remove()}>{deleting ? "正在删除…" : "确认删除"}</button><button type="button" className="text-button" onClick={() => setDeleteConfirm(false)}>取消</button></div>
          )}
        </div>
      ) : null}
    </form>
  );
}
